/**
 * Google-Docs-style relative timestamp label for table date columns:
 * - same calendar day: time only, e.g. "3:04 PM"
 * - earlier this year: month + day, e.g. "Jul 23"
 * - a prior year: month + day + year, e.g. "Mar 25, 2025"
 *
 * Returns "" for a missing or unparseable value so callers can fall back.
 * `now` is injectable for deterministic tests.
 */
export function formatRelativeTimestamp(
  dateStr: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!dateStr) {
    return ""
  }
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) {
    return ""
  }

  const sameYear = date.getFullYear() === now.getFullYear()
  const sameDay =
    sameYear &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (sameDay) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }
  if (sameYear) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Like formatRelativeTimestamp, but always keeps the time of day alongside the
 * date, e.g. "Jul 23, 3:04 PM" or "Mar 25, 2025, 3:04 PM". Same-day values are
 * already time-only, so they are returned unchanged. Use where the time is the
 * clearest way to tell two rows apart, e.g. the conversations "Uploaded" column.
 *
 * Returns "" for a missing or unparseable value so callers can fall back.
 * `now` is injectable for deterministic tests.
 */
export function formatRelativeTimestampWithTime(
  dateStr: string | null | undefined,
  now: Date = new Date(),
): string {
  const relative = formatRelativeTimestamp(dateStr, now)
  if (!relative) {
    return ""
  }

  const date = new Date(dateStr as string)
  const sameYear = date.getFullYear() === now.getFullYear()
  const sameDay =
    sameYear &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (sameDay) {
    return relative
  }

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
  return `${relative}, ${time}`
}

/**
 * Full absolute timestamp for hover tooltips, e.g. "Jul 23, 2026, 3:04 PM PDT".
 * Returns "" for a missing or unparseable value.
 */
export function formatFullTimestamp(
  dateStr: string | null | undefined,
): string {
  if (!dateStr) {
    return ""
  }
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) {
    return ""
  }
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  })
}
